<template>
    <div class="w-full mb-8 relative">
        <div v-if="loading2" class="lds-spinner">
            <div /><div /><div /><div /><div /><div /><div /><div /><div /><div /><div /><div />
        </div>
        <div class="text-left m-8 text-sm ">
            <p>The following documents must be provided in order for your application to be considered:</p>
            <ul class="list-disc my-2 mx-4">
                <li>A copy of your valid international passport (photo page) or EU National ID card</li>
                <li>Any visa or immigration documents (including BRP cards or EU settlement status) confirming your right to remain in the UK (if applicable)</li>
                <li>CV/resume</li>
                <li>Official academic transcripts for any completed or in-progress degrees</li>
                <li>Degree certificates</li>
                <li>Certificates for relevant professional qualifications</li>
                <li>Evidence of English language ability (if applicable)</li>
                <li>PhD and DrPH applicants: research degree proposal (mandatory)</li>
                <li>DrPH applicants: organisational policy analysis (mandatory)</li>
            </ul>
        </div>
        <div class="text-left m-8 text-sm ">
            <p class="mb-2">If you are applying for a scholarship and the advertisement on the LSHTM website requests that you upload a Scholarships Application form – please upload it in the Supporting Documents Section along with any other supporting documents you are providing.</p>
            <p>Please select 'Scholarships Form' from the drop down menu in the document type. Please note that scholarships forms <b>must</b> be provided prior to the <b>scholarship deadline</b> stated in the advert.</p>
        </div>
        <div class="text-left m-8 text-sm ">
            <p>You can submit your application and provide these documents at a later date, but please be advised that this will delay your application being processed.</p>
            <p>You can upload the following file types (maximum file size 5MB):</p>
            <ul class="list-disc my-2 mx-4">
                <li>Text documents (.txt)</li>
                <li>Microsoft Word documents (.doc, .docx)</li>
                <li>Rich text documents (.rtf)</li>
                <li>Adobe Acrobat documents (.pdf)</li>
            </ul>
        </div>
        <div v-if="!documentation || documentation.length > 0" class="text-sm mx-8 p-8 flex flex-col items-center border border-gray-300 bg-gray-100">
            <table class="w-full">
                <thead>
                    <tr>
                        <th>Document Type</th>
                        <th>Description</th>
                        <th>File name</th>
                        <th>View</th>
                        <th>Delete</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(document, index) in documentation" :key="index" class="hover:bg-gray-100 focus-within:bg-gray-100">
                        <td class="border-b px-5 py-2 font-medium">{{ document['type'] }}</td>
                        <td class="border-b px-5 py-2">{{ document['document_description'] }}</td>
                        <td class="border-b px-5 py-2">{{ document['original_filename'] }}</td>
                        <td class="border-b px-5 py-2 ">
                            <button class="text-sm p-2 rounded bg-green-400 items-center text-white inline-block" tabindex="-1" type="button"
                                    @click="download(document['reference'], document['original_filename'])"
                            >
                                View document
                            </button>
                        </td>
                        <td class="border-b px-5 py-2 ">
                            <button class="text-sm p-2 rounded bg-red-600 items-center text-white inline-block" tabindex="-1" type="button"
                                    @click="destroy(document['reference'])"
                            >
                                Delete document
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-if="successMessage2" class="success-message mt-4 w-full lg:w-1/2">{{ successMessage2 }}</div>
            <div v-if="serverError2" class="server-error w-full lg:w-1/2">{{ serverError2 }}</div>
        </div>
        <div v-else class="p-8 flex flex-col items-center">
            <p>There are no documents uploaded to this application</p>
        </div>
        <div class="text-sm mx-8 mt-2 border border-gray-300 bg-gray-100">
            <form @submit.prevent="update" @change="dirtyForm()">
                <div class="p-4 md:p-8 flex flex-col items-center">
                    <div class="w-full lg:w-1/2 text-left">
                        <file-input v-model="form.file" :value="form.file" type="file" label="Select document to upload" @remove="form.file = value"
                                    @change="form.file = $event.target.files[0]"
                        />
                    </div>
                    <div v-if="!isReferenceDocument" class="w-full lg:w-1/2 mt-4">
                        <select-input id="document_type" :key="documentTypeKey" v-model="form.document_type" :custom-class="customClass"
                                      label="Select document type" @update:modelValue="value = $event"
                        >
                            <option v-for="(name, reference) in documentTypes" :key="reference" :value="reference">{{ name }}</option>
                        </select-input>
                    </div>

                    <div class="w-full lg:w-1/2 text-left">
                        <textarea-input
                            v-model="form.description"
                            class="w-full bg-green-200 padding-2 mb-1"
                            label="Description of the document (optional)"
                            rows="2"
                            @update:modelValue="value = $event"
                        />
                    </div>
                    <div v-if="successMessage" class="success-message mt-4 w-full lg:w-1/2">{{ successMessage }}</div>
                    <div v-if="serverError" class="server-error w-full lg:w-1/2">{{ serverError }}</div>
                    <div class="flex flex-col items-center w-full lg:w-1/2">
                        <button class="text-sm w-full lg:w-1/2 flex btn-green ml-auto -mt-0 md:inline-block text-center relative"
                                type="submit"
                                :disabled="!form.file || !form.document_type"
                                :class="{ 'opacity-25 cursor-not-allowed': !form.file || !form.document_type }"
                                tabindex="-1"
                        >
                            Upload document
                            <div v-if="loading" class="lds-ring-container absolute top-2 left-4 "><div class="lds-ring"><div /><div /><div /><div /></div></div>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import FileInput from '@/shared/FileInput'
import SelectInput from '@/shared/SelectInput'
import TextareaInput from '@/shared/TextareaInput'

export default {
    components: {
        FileInput,
        SelectInput,
        TextareaInput,
    },
    props: {
        documentation: Object,
        documentTypes: Object,
        applicationRef: String,
        isReferenceDocument: {
            type: Boolean,
            default: false,
        },
        referenceDocType: {
            type: String,
        },
    },
    data() {
        return {
            form: ({
                reference: this.applicationRef,
                document_type: null,
                description: null,
                file: null,
            }),
            formDirty: false,
            loading: false,
            loading2: false,
            successMessage: '',
            serverError: '',
            successMessage2: '',
            serverError2: '',
            documentTypeKey: 1000,
            customClass: 'w-full-i',
        }
    },
    mounted() {
        if (this.isReferenceDocument) {
            this.form.document_type = this.referenceDocType
        }
    },
    methods: {
        update() {
            this.loading = true
            const formData = new FormData()
            formData.append('file', this.form.file)
            formData.append('reference', this.applicationRef)
            formData.append('document_type', this.form.document_type)
            formData.append('description', this.form.description)
            this.$store.dispatch('uploadDocument', {
                applicationRef: this.applicationRef,
                file: formData,
            })
                .then((response) => {
                    this.loading = false
                    this.successMessage = response.data
                    this.serverError = ''
                    window.location.search += '&section=documentationShow'
                })
                .catch(() => {
                    this.loading = false
                    this.successMessage = ''
                    this.serverError = 'Something went wrong, please try again'
                })


        },
        download(reference, fileName) {
            const formData = new FormData()
            formData.append('reference', this.form.reference)
            formData.append('file', this.form.file)

            let documentType = 'downloadDocument'
            if (this.isReferenceDocument) {
                documentType = 'downloadReferenceDocument'
            }
            this.$store.dispatch(documentType, {
                applicationRef: this.applicationRef,
                documentRef: reference,
                file: formData,
                fileName: fileName,
                referrerRef: this.$route.params.id,
                token: this.$route.params.token,
            })
                .then(() => {
                    this.successMessage2 = 'Document downloaded'
                    this.serverError2 = ''
                })
                .catch(error => {
                    this.successMessage2 = ''
                    this.serverError2 = 'Something went wrong, please try again'
                })

        },
        dirtyForm() {
            this.formDirty = true
            this.successMessage = ''
        },
        destroy(reference) {
            if (confirm('Are you sure you want to delete this document?')) {

                this.loading2 = true

                    this.$store.dispatch('deleteDocument', {
                        documentRef: reference,
                    })
                        .then((response) => {
                            this.loading2 = false
                            this.serverErrors2 = ''
                                this.successMessage2 = response.data
                            window.location.search += '&section=documentationShow'
                        })
                        .catch(error => {
                            this.loading2 = false
                            this.serverError2 = 'Something went wrong, please try again'
                            this.successMessage2 = ''
                        })


                this.formDirty = false
            }
        },
    },
}
</script>
<style scoped>
.w-full-i {
    width: 100% !important;
    padding-right: 0;
}
</style>